/**
 * AssignExistingNumberDialog
 *
 * Per-account flow for assigning a Telnyx-account number that's already on the
 * carrier (or already in another tenant's pool) to THIS tenant. Hides any
 * number already assigned to this tenant via `excludeAssignedTenantId`.
 *
 * Lives next to the "Provision number" button on PhoneNumbersTab and shares
 * the same backend endpoint as the bulk inventory page.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  Phone,
  Check,
  X,
  Search,
  Users,
  AlertTriangle,
  MessageSquare,
  Image as ImageIcon,
  Mic,
} from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  cn,
} from '@respectly/ui';
import { useNumberInventoryStore } from '../../../stores/useNumberInventoryStore';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The tenant the operator is currently inside on the Phone Numbers tab. */
  tenantId: string;
  onAssigned?: () => void;
}

function formatE164(num: string): string {
  const m = num.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (m) return `+1 (${m[1]}) ${m[2]}-${m[3]}`;
  return num;
}

export function AssignExistingNumberDialog({ open, onClose, tenantId, onAssigned }: Props) {
  const { items, isLoading, loadError, isAssigning, load, assignToTenants } =
    useNumberInventoryStore();

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set()); // telnyx ids
  const [setAsDefault, setSetAsDefault] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelected(new Set());
    setSetAsDefault(false);
    // Pull only numbers NOT yet assigned to this tenant.
    load({ excludeAssignedTenantId: tenantId });
  }, [open, tenantId, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        i.telnyx.phoneNumber.includes(q) ||
        i.telnyx.phoneNumber.replace(/\D/g, '').includes(q.replace(/\D/g, '')) ||
        i.local?.friendlyName?.toLowerCase().includes(q),
    );
  }, [items, search]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selected.size === 0) return;
    let allOk = true;
    let firstId: string | null = null;
    for (const telnyxId of selected) {
      const item = items.find((i) => i.telnyx.id === telnyxId);
      if (!item) continue;
      if (!firstId) firstId = telnyxId;
      const ok = await assignToTenants({
        telnyx: item.telnyx,
        monthlyFee: item.local?.monthlyFee,
        tenantIds: [tenantId],
        setDefaultForTenantId: setAsDefault ? tenantId : null,
      });
      if (!ok) allOk = false;
    }
    if (allOk) {
      onAssigned?.();
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-slate-500" />
            Assign existing number
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-slate-500">
          Pick one or more numbers from your Telnyx account. Selected numbers will be added to
          this tenant&apos;s sender pool. Numbers already assigned to other tenants will become{' '}
          <span className="font-semibold">shared</span> across both.
        </p>

        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by number or label…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm"
          />
        </div>

        {loadError && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/30 dark:text-rose-300">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{loadError.message}</span>
            </div>
          </div>
        )}

        <div className="max-h-[360px] overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-800">
          {isLoading && items.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              {items.length === 0
                ? 'No Telnyx numbers available to assign.'
                : 'No numbers match your search.'}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {filtered.map((item) => (
                <li
                  key={item.telnyx.id}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 text-sm transition-colors',
                    selected.has(item.telnyx.id)
                      ? 'bg-sky-50/60 dark:bg-sky-950/20'
                      : 'hover:bg-slate-50/60 dark:hover:bg-slate-900/40',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggle(item.telnyx.id)}
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                      selected.has(item.telnyx.id)
                        ? 'border-sky-500 bg-sky-500 text-white'
                        : 'border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900',
                    )}
                  >
                    {selected.has(item.telnyx.id) && <Check className="h-3 w-3" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm tabular-nums font-semibold text-slate-900 dark:text-white">
                        {formatE164(item.telnyx.phoneNumber)}
                      </span>
                      <span className="text-[11px] uppercase tracking-wide text-slate-400">
                        {item.telnyx.type === 'tollfree' ? 'Toll-free' : item.telnyx.type}
                      </span>
                      {item.assignments.length > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-md bg-violet-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700 ring-1 ring-violet-200/60 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-800/50">
                          <Users className="h-3 w-3" />
                          Used by {item.assignments.length}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-1">
                      <CapPill on={item.telnyx.capabilities.sms} icon={MessageSquare} label="SMS" />
                      <CapPill on={item.telnyx.capabilities.mms} icon={ImageIcon} label="MMS" />
                      <CapPill on={item.telnyx.capabilities.voice} icon={Mic} label="Voice" />
                      {item.local?.friendlyName && (
                        <span className="ml-1 text-xs text-slate-500">
                          · {item.local.friendlyName}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Default toggle */}
        <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-400">
          <input
            type="checkbox"
            checked={setAsDefault}
            onChange={(e) => setSetAsDefault(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-slate-300 dark:border-slate-700"
          />
          Set as this account&apos;s default sender (only one number can be default per account)
        </label>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isAssigning}>
            <X className="mr-1.5 h-4 w-4" />
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selected.size === 0 || isAssigning}
            className="bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            {isAssigning ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-1.5 h-4 w-4" />
            )}
            Assign {selected.size > 0 ? `(${selected.size})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CapPill({
  on,
  icon: Icon,
  label,
}: {
  on: boolean;
  icon: typeof MessageSquare;
  label: string;
}) {
  return (
    <span
      title={`${label}: ${on ? 'enabled' : 'disabled'}`}
      className={cn(
        'inline-flex h-5 items-center gap-1 rounded-md px-1.5 text-[10px] font-bold uppercase tracking-wide ring-1',
        on
          ? 'bg-sky-50 text-sky-700 ring-sky-200/60 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800/50'
          : 'bg-slate-50 text-slate-300 ring-slate-200 dark:bg-slate-900/40 dark:text-slate-600 dark:ring-slate-800',
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}
